import {useCallback, useEffect, useRef, useState} from "react";
import React from 'react';
import {RiVolumeDownFill, RiVolumeMuteFill, RiVolumeUpFill} from "react-icons/ri";

const VolumeRangeSlider = ({ min = 0, max = 5, step = 1, initialValue, onChange, isOn}) => {
  const [value, setValue] = useState(initialValue);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const prevIsOnRef = useRef(isOn);

  const percentage = ((value - min) / (max - min)) * 100;

  // isOn 상태가 변경될 때 볼륨 자동 조절
  useEffect(() => {
    // 이전 상태와 현재 상태가 다를 때만 실행
    if (prevIsOnRef.current !== isOn) {
      // 옵션이 ON 이면 볼륨 0, OFF 이면 3
      const newVolume = isOn ? 3 : 0;
      setValue(newVolume);
      onChange(newVolume);
      prevIsOnRef.current = isOn;
    }
  }, [isOn]);

  const updateValue = useCallback((clientX) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const newPercentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const newValue = Math.round((newPercentage / 100) * (max - min) / step) * step + min;
    const clampedValue = Math.max(min, Math.min(max, newValue));

    if (clampedValue !== value) {
      setValue(clampedValue);
      onChange(clampedValue);
    }
  }, [min, max, step, value, onChange]);

  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      updateValue(e.clientX);
    }
  }, [isDragging, updateValue]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTrackClick = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    updateValue(e.clientX);
  }, [updateValue]);

  const handleTouchStart = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (isDragging) {
      updateValue(e.touches[0].clientX);
    }
  }, [isDragging, updateValue]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
      <div className="space-y-4 mt-5">
        <div className="flex items-center gap-2 mt-2">
          {value === 0 ? (
              <RiVolumeMuteFill className="text-gray-500 w-5 h-5"/>
          ) : value <= 3 ? (
              <RiVolumeDownFill className="text-violet-600 w-5 h-5"/>
          ) : (
              <RiVolumeUpFill className="text-violet-600 w-5 h-5"/>
          )}

          <span className="text-sm text-gray-700">볼륨: {value}</span>
        </div>


        <div className="relative">
          <div
              ref={sliderRef}
              className="relative h-3 rounded-full bg-gray-300 cursor-pointer"
              onClick={handleTrackClick}
          >
            <div
                className="absolute top-0 left-0 z-10 h-full bg-gradient-to-r from-violet-400 to-violet-600 rounded-full transition-all duration-200 ease-out"
                style={{width: `${percentage}%`}}
            />

            <div
                className="absolute top-1/2 z-20 w-5 h-5 bg-white border-4 border-violet-500 rounded-full cursor-pointer transform -translate-y-1/2 transition-all duration-200 ease-out hover:scale-110 active:scale-95 shadow-xl ring-2 ring-violet-200"
                style={{
                  left: `${percentage}%`,
                  transform: 'translateX(-50%) translateY(-50%)'
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            />
          </div>

          <input
              type="range"
              className="sr-only"
              min={min}
              max={max}
              step={step}
              value={value}
              readOnly
          />
        </div>

        <div className="flex justify-between text-xs text-gray-500 px-2">
          {Array.from({length: max - min + 1}, (_, i) => (
              <span key={i}>{i + min}</span>
          ))}
        </div>
      </div>
  );
};

export default VolumeRangeSlider;